/**
 * @file: TagOptionMenuItem.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { Typography } from '@euler/app/components/typography';
import { Icon } from '@euler/components/Icomoon';
import {
  getDefectiveLevel,
  getDefectiveLevelBgColor,
  getDefectiveLevelColor,
  getDefectiveLevelText,
} from '@euler/functions/getDefectiveLevel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { memo, useCallback } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { useTheme } from 'styled-components/native';
import { AnnotationTagOption } from '../functions/AnnotationTagOption';

export const TagOptionMenuItem = memo(
  ({
    option,
    selected,
    isFirst,
    onPress,
  }: {
    option: AnnotationTagOption;
    selected?: boolean;
    /** injected by the enclosing `Menu` */
    isFirst?: boolean;
    onPress: (option: AnnotationTagOption) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const theme = useTheme();

    const level = getDefectiveLevel(option.abnormalLevel, option.severityLevel);
    const color = getDefectiveLevelColor(theme, level);

    const onItemPress = useCallback(() => {
      onPress(option);
    }, [onPress, option]);

    return (
      <View
        css={`
          height: ${rpx(44)}px;
          border-top-width: ${StyleSheet.hairlineWidth}px;
          border-top-color: ${isFirst ? 'transparent' : '#cccccc'};
        `}
      >
        <TouchableOpacity
          onPress={onItemPress}
          css={`
            flex: 1;
            flex-direction: row;
            align-items: center;
            padding: 0 ${rpx(15)}px;
          `}
        >
          <Typography.Label
            size={rpx(15)}
            family={selected ? 'medium' : undefined}
            css={`
              flex: 1;
              margin-right: ${rpx(10)}px;
            `}
          >
            {option.label}
          </Typography.Label>

          <View
            css={`
              border-radius: ${rpx(999)}px;
              padding: ${rpx(3)}px ${rpx(8)}px;
              background-color: ${getDefectiveLevelBgColor(theme, level, 0.15)};
            `}
          >
            <Typography.Label size={rpx(12)} color={color}>
              {getDefectiveLevelText(level)}
            </Typography.Label>
          </View>

          <View
            css={`
              width: ${rpx(20)}px;
              align-items: flex-end;
            `}
          >
            {selected ? (
              <Icon name="check" size={rpx(16)} color={color} />
            ) : null}
          </View>
        </TouchableOpacity>
      </View>
    );
  },
);
